import { Timer, Utensils, ShoppingBag, Bike } from 'lucide-react';
import { Trans } from '@lingui/react/macro';
import { useLingui } from '@lingui/react/macro';
import { Badge } from '@/components/ui/badge';
import { ProgressRing as UiProgressRing } from '@/components/ui/progress-ring';
import { StatusPill as UiStatusPill } from '@/components/ui/status-pill';
import { TimeChip } from '@/components/ui/time-chip';
import { initials } from '@/components/ui/chef-avatar';
import { cn } from '@/lib/utils';
import type { BoardOrder, ServiceType, DeckPriority } from '@/lib/api/services/orders.api';

// ─── Derived display state ────────────────────────────────────────────────────

export type OrderState = 'cooking' | 'late' | 'paused' | 'plating' | 'ready' | 'enroute' | 'done';

/** Derive a display state from a BoardOrder for a given board stage. */
export function deriveBoardState(order: Pick<BoardOrder, 'isActive' | 'isLate' | 'isPaused'>, stage: 'cooking' | 'plating' | 'pickup' | 'delivery' | 'completed'): OrderState {
  if (stage === 'cooking') {
    if (order.isLate) return 'late';
    if (order.isPaused) return 'paused';
    return 'cooking';
  }
  if (stage === 'plating') return 'plating';
  if (stage === 'pickup') return 'ready';
  if (stage === 'delivery') return 'enroute';
  return 'done';
}

// Re-export for convenience so callers don't need to import from orders.api
export type { ServiceType, DeckPriority };

// ─── Helpers ──────────────────────────────────────────────────────────────────

export { initials };

// ─── State pill ───────────────────────────────────────────────────────────────
// Matches the StatusPill pattern in CookingNowSection: solid bg + white text,
// using CSS vars from tokens.css.

export function StatePill({ state }: { state: OrderState }) {
  const { t } = useLingui();
  const LABELS: Record<OrderState, string> = {
    cooking: t`Cooking`,
    late:    t`Late`,
    paused:  t`Paused`,
    plating: t`Plating`,
    ready:   t`Ready`,
    enroute: t`En route`,
    done:    t`Done`,
  };
  return (
    <UiStatusPill variant="solid" status={state}>
      {LABELS[state]}
    </UiStatusPill>
  );
}

// ─── Priority pill ────────────────────────────────────────────────────────────
// Matches PriorityPill in OnDeckSection.

const PRIO_STATUS = {
  HIGH: 'high',
  NORMAL: 'normal',
  LOW: 'low',
} as const;

export function PrioPill({ prio }: { prio: DeckPriority }) {
  const { t } = useLingui();
  const LABELS: Record<DeckPriority, string> = {
    HIGH:   t`High`,
    NORMAL: t`Normal`,
    LOW:    t`Low`,
  };
  return (
    <UiStatusPill variant="solid" status={PRIO_STATUS[prio]}>
      {LABELS[prio]}
    </UiStatusPill>
  );
}

// ─── Service chip ─────────────────────────────────────────────────────────────

const SVC_ICON: Record<ServiceType, React.ComponentType<{ className?: string }>> = {
  DINE_IN:  Utensils,
  TAKEOUT:  ShoppingBag,
  DELIVERY: Bike,
};

export function ServiceChip({ service }: { service: ServiceType }) {
  const { t } = useLingui();
  const Icon = SVC_ICON[service];
  const label = service === 'DINE_IN' ? t`Dine-in`
    : service === 'TAKEOUT' ? t`Takeout` : t`Delivery`;
  return (
    <span className="inline-flex items-center gap-1 text-[0.75rem] font-semibold text-(--color-text-primary) bg-(--color-surface-muted) border border-(--color-border) px-2 py-0.5 rounded-full">
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
}

// ─── Timer chip ───────────────────────────────────────────────────────────────
// Matches EtaPill in OnDeckSection.

export function TimerChip({ label, icon: Icon }: {
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
}) {
  const Ic = Icon ?? Timer;
  return (
    <TimeChip
      icon={<Ic className="w-3.5 h-3.5 text-(--color-text-secondary)" />}
      className="text-(--color-text-primary)"
    >
      {label}
    </TimeChip>
  );
}

// ─── Chef initials avatar ─────────────────────────────────────────────────────

export function ChefAvatar({ name, className }: { name: string; className?: string }) {
  return (
    <span className={cn(
      'inline-flex items-center justify-center rounded-full bg-(--brand-blue)/15 text-(--brand-blue) text-[0.5625rem] font-bold shrink-0',
      className,
    )}>
      {initials(name)}
    </span>
  );
}

// ─── Progress ring ────────────────────────────────────────────────────────────
// Uses CSS vars for stroke colors, matching ProgressCircle in CookingNowSection.

export function ProgressRing({ pct, state }: { pct: number; state: OrderState }) {
  const tone =
    state === 'late'   ? 'late' :
    state === 'paused' ? 'warning' :
    pct >= 95          ? 'warning' :
                         'brand';
  return <UiProgressRing pct={pct} size="md" tone={tone} />;
}

// ─── Section header ───────────────────────────────────────────────────────────

export function SectionHeader({
  title,
  badgeLabel,
  badgeClass,
  onViewAll,
}: {
  title: string;
  badgeLabel: string;
  badgeClass?: string;
  onViewAll?: () => void;
}) {
  return (
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2">
        <span className="text-lg font-semibold text-(--color-text-primary)">{title}</span>
        <Badge variant="neutral" className={cn('rounded-full text-xs', badgeClass)}>{badgeLabel}</Badge>
      </div>
      {onViewAll && (
        <button
          type="button"
          onClick={onViewAll}
          className="text-sm font-medium text-(--color-text-secondary) hover:text-(--color-text-primary)"
        >
          <Trans>View all</Trans>
        </button>
      )}
    </div>
  );
}
